import {GroupMember, UserSmall, createGroupMemberFromUser} from '@/features/users/types'
import {ConversationMember} from '@/features/conversations/types'
import {SelectedItem} from '@/features/users/components/users-and-groups-multi-select-popover/UsersAndGroupsMultiSelectPopover'
import i18n from 'i18next'

/**
 * Gets the display name for a conversation member (user or group)
 */
export const getConversationMemberDisplayName = (member: ConversationMember): string => {
    if (member.type === 'user' && member.fullName) {
        return member.fullName
    }
    if (member.type === 'group' && member.name) {
        return member.name
    }
    return '' // Fallback
}

/**
 * Converts ConversationMember array to GroupMember array for consistency
 */
export const convertConversationMembersToGroupMembers = (members: ConversationMember[]): GroupMember[] => {
    return members.map(member => {
        const displayName = getConversationMemberDisplayName(member)
        const nameParts = displayName ? displayName.split(' ') : ['', '']
        return {
            id: member.id,
            firstName: nameParts[0] || '',
            lastName: nameParts.slice(1).join(' ') || '',
            email: '',
            avatarUrl: null
        }
    })
}

/**
 * Formats the display text for participants
 */
export const getParticipantsDisplayText = (
    participants: GroupMember[],
    emptyText: string = 'conversations:select_participants'
): string => {
    switch (participants.length) {
        case 0:
            return i18n.t(emptyText)
        case 1:
            return `${participants[0].firstName} ${participants[0].lastName}`
        case 2:
            return `${participants[0].firstName} ${participants[0].lastName} e ${participants[1].firstName} ${participants[1].lastName}`
        default:
            return `${i18n.t('conversations:you_and_others', {count: participants.length - 1})}`
    }
}

/**
 * Determines selected items from conversation members for the dropdown
 */
export const determineSelectedItems = (participants: ConversationMember[]): SelectedItem[] => {
    return participants.map(participant => ({
        type: participant.type as 'user' | 'group',
        id: participant.id
    }))
}

/**
 * Creates a toggle handler for user selection in participant lists
 */
export const createUserToggleHandler = (
    selectedMembers: GroupMember[],
    onMembersChange: (members: GroupMember[]) => void,
    excludeMembers: GroupMember[] = [],
    onMemberAdded?: (memberId: string) => void
) => {
    return (user: UserSmall) => {
        const member = createGroupMemberFromUser({
            id: user.id,
            fullName: user.fullName,
            email: user.email ?? '',
            avatarUrl: user.avatarUrl ?? null
        })

        const selectedMemberIds = selectedMembers?.map(m => m.id) || []
        const isSelected = selectedMemberIds.includes(member.id)
        const isExcluded = excludeMembers.some(m => m.id === member.id)

        if (isSelected) {
            // Remove from selection
            const newMembers = selectedMembers?.filter(m => m.id !== member.id) || []
            onMembersChange(newMembers)
        } else if (!isExcluded) {
            // Add to selection only if not excluded
            const newMembers = [...(selectedMembers || []), member]
            onMembersChange(newMembers)

            // Call callback when member is added
            if (onMemberAdded) {
                onMemberAdded(member.id)
            }
        }
    }
}
